import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Download, ArrowRight, Eye, Calendar, MapPin, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SharedSowPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/scope/shared-sow/${token}`);
        setData(res.data);
      } catch (e) {
        setError(e.response?.data?.error || 'This share link is no longer available.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#F7F8FA' }}>
        <div style={{ fontSize: 14, color: '#64748B' }} data-testid="shared-sow-loading">Loading shared document…</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#F7F8FA', padding: 24 }}>
        <div style={{
          maxWidth: 440, background: '#fff', borderRadius: 14, padding: 28,
          boxShadow: '0 4px 20px rgba(15,39,66,.05)', textAlign: 'center',
        }} data-testid="shared-sow-expired">
          <AlertCircle size={32} style={{ color: '#94A3B8', margin: '0 auto 12px' }} />
          <h1 style={{ fontFamily: "'DM Serif Display',serif", fontSize: 24, color: '#0F2742', margin: '0 0 8px' }}>
            Link unavailable
          </h1>
          <p style={{ color: '#64748B', fontSize: 14, margin: '0 0 20px', lineHeight: 1.55 }}>{error}</p>
          <a
            href="/estimate"
            data-testid="expired-estimate-cta"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: '#2563EB', color: '#fff', padding: '12px 22px',
              borderRadius: 10, textDecoration: 'none', fontSize: 14, fontWeight: 700,
            }}
          >
            Get your own free estimate <ArrowRight size={16} />
          </a>
        </div>
      </div>
    );
  }

  const {
    order_ref, address, postcode, output_range_low, output_range_high,
    region, rooms_count, breakdown, downloads, shared_by, view_count, expires_at,
  } = data;

  const expiresNice = expires_at
    ? new Date(expires_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : '';

  return (
    <div style={{ minHeight: '100vh', background: '#F7F8FA', paddingBottom: 48 }} data-testid="shared-sow-page">
      {/* Contractor acquisition banner */}
      <div
        data-testid="shared-sow-banner"
        style={{
          background: 'linear-gradient(135deg, #0F2742 0%, #1E3A5F 100%)',
          color: '#fff', padding: '14px 24px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 16, flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 280 }}>
          <div style={{
            width: 34, height: 34, borderRadius: 8, background: '#2563EB',
            display: 'grid', placeItems: 'center', flexShrink: 0,
          }}>
            <Eye size={16} style={{ color: '#fff' }} />
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.12em',
                          color: '#93C5FD', textTransform: 'uppercase', marginBottom: 2 }}>
              Shared view · read-only
            </div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,.85)' }}>
              This Schedule of Works was created on Schedules of Work — get your own free estimate
            </div>
          </div>
        </div>
        <a
          href="/estimate"
          data-testid="banner-estimate-cta"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: '#2563EB', color: '#fff', padding: '10px 20px',
            borderRadius: 9, textDecoration: 'none', fontSize: 13, fontWeight: 700,
            whiteSpace: 'nowrap',
          }}
        >
          Get your free estimate <ArrowRight size={14} />
        </a>
      </div>

      <div style={{ maxWidth: 900, margin: '40px auto', padding: '0 24px' }}>
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.14em',
                      color: '#64748B', textTransform: 'uppercase', marginBottom: 10 }}>
          {order_ref}
        </div>
        <h1 style={{ fontFamily: "'DM Serif Display',serif", fontSize: 36, color: '#0F2742',
                     margin: '0 0 10px', letterSpacing: '-0.01em' }}>
          Schedule of Works — shared document
        </h1>
        <p style={{ color: '#64748B', fontSize: 14, margin: 0 }}>
          Shared by <strong>{shared_by}</strong> · {view_count} {view_count === 1 ? 'view' : 'views'} · expires {expiresNice}
        </p>

        {/* Project summary card */}
        <div style={{ background: '#fff', borderRadius: 14, padding: 28, marginTop: 28,
                      border: '1.5px solid #E2E8F0' }} data-testid="shared-sow-summary">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                        gap: 20, marginBottom: 20 }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.12em',
                            color: '#64748B', textTransform: 'uppercase', marginBottom: 4 }}>
                <MapPin size={11} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
                Location
              </div>
              <div style={{ fontSize: 14, color: '#0F2742', fontWeight: 600 }}>
                {address || '—'}{postcode ? `, ${postcode}` : ''}
              </div>
              {region && (
                <div style={{ fontSize: 12, color: '#64748B', marginTop: 2 }}>
                  {region}
                </div>
              )}
            </div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.12em',
                            color: '#64748B', textTransform: 'uppercase', marginBottom: 4 }}>
                Budget range
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#0F2742',
                            fontFamily: "'DM Serif Display',serif" }}>
                £{Number(output_range_low || 0).toLocaleString('en-GB')}
                <span style={{ color: '#64748B', fontSize: 14, fontWeight: 400 }}> – </span>
                £{Number(output_range_high || 0).toLocaleString('en-GB')}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.12em',
                            color: '#64748B', textTransform: 'uppercase', marginBottom: 4 }}>
                Rooms
              </div>
              <div style={{ fontSize: 14, color: '#0F2742', fontWeight: 600 }}>
                {rooms_count > 0 ? `${rooms_count} rooms included` : '—'}
              </div>
            </div>
          </div>

          {/* Cost breakdown table */}
          {breakdown && breakdown.length > 0 && (
            <div style={{ borderTop: '1px solid #E2E8F0', paddingTop: 18, marginTop: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: '#0F2742', marginBottom: 12 }}>
                Cost breakdown
              </div>
              <table data-testid="shared-sow-breakdown" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <tbody>
                  {breakdown.map((section, i) => (
                    <tr key={i} style={{ borderTop: i > 0 ? '1px solid #F1F5F9' : 'none' }}>
                      <td style={{ padding: '10px 0', color: '#0F2742', fontWeight: 500 }}>{section.name || section.title || ''}</td>
                      <td style={{ padding: '10px 0', color: '#64748B', textAlign: 'right' }}>
                        {section.total_low != null && section.total_high != null
                          ? `£${Number(section.total_low).toLocaleString('en-GB')} – £${Number(section.total_high).toLocaleString('en-GB')}`
                          : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Document downloads */}
        {downloads && Object.keys(downloads).length > 0 && (
          <div style={{ background: '#fff', borderRadius: 14, padding: 24, marginTop: 20,
                        border: '1.5px solid #E2E8F0' }} data-testid="shared-sow-downloads">
            <h2 style={{ fontSize: 14, fontWeight: 800, color: '#0F2742', margin: '0 0 14px',
                         textTransform: 'uppercase', letterSpacing: '.08em' }}>
              Documents
            </h2>
            <div style={{ display: 'grid', gap: 8 }}>
              {Object.entries(downloads).map(([type, info]) => (
                <a
                  key={type}
                  href={info.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`shared-sow-download-${type}`}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '12px 14px', background: '#F7F8FA',
                    border: '1px solid #E2E8F0', borderRadius: 9,
                    textDecoration: 'none', color: '#0F2742', fontSize: 13, fontWeight: 600,
                  }}
                >
                  <span>{info.filename}</span>
                  <Download size={15} style={{ color: '#2563EB' }} />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Closing banner */}
        <div style={{
          background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)',
          border: '2px solid #2563EB', borderRadius: 14, padding: 28, marginTop: 28,
          textAlign: 'center',
        }} data-testid="shared-sow-footer-cta">
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.12em', color: '#1D4ED8',
                        textTransform: 'uppercase', marginBottom: 8 }}>
            Want your own Schedule of Works?
          </div>
          <h2 style={{ fontFamily: "'DM Serif Display',serif", fontSize: 22, color: '#0F2742', margin: '0 0 10px' }}>
            Built for homeowners. Trusted by contractors.
          </h2>
          <p style={{ fontSize: 13, color: '#475569', maxWidth: 480, margin: '0 auto 18px', lineHeight: 1.6 }}>
            Get an indicative estimate for your own project — free. Upgrade to a full Schedule of Works when you're ready.
          </p>
          <Link
            to="/estimate"
            data-testid="footer-estimate-cta"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: '#2563EB', color: '#fff', padding: '14px 28px',
              borderRadius: 10, textDecoration: 'none', fontSize: 14, fontWeight: 700,
            }}
          >
            Start your free estimate <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </div>
  );
}
